import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Sparkles, Calendar, Users, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { PROJECTS } from "@/constants/testIds";

const HEALTH_COLORS = { Healthy: "#10B981", Monitor: "#3B82F6", "At Risk": "#F59E0B", Critical: "#EF4444" };

function fmtMoney(n) {
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n}`;
}

export default function ProjectDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [ai, setAi] = useState("");
    const [aiBusy, setAiBusy] = useState(false);

    useEffect(() => { api.get(`/projects/${id}`).then((r) => setData(r.data)); }, [id]);

    const runHealth = async () => {
        setAiBusy(true);
        setAi("");
        try {
            const { data: r } = await api.post("/ai/task", {
                kind: "project_health",
                payload: { project_code: data.project.code },
            });
            setAi(r.result);
            toast.success("Project health assessment ready");
        } catch {
            toast.error("AI task failed");
        } finally {
            setAiBusy(false);
        }
    };

    if (!data) return <div className="kicker text-slate-500">Loading project…</div>;
    const { project, packages, contracts, deliverables, rfis } = data;

    return (
        <div data-testid={PROJECTS.detailRoot} className="space-y-6">
            <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[var(--sgsb-gold)]">
                <ArrowLeft className="h-3 w-3" /> Back to projects
            </Link>

            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.18), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.14), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-3">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]"
                            style={{
                                background: `${HEALTH_COLORS[project.health]}22`,
                                color: HEALTH_COLORS[project.health],
                                border: `1px solid ${HEALTH_COLORS[project.health]}55`,
                            }}
                        >
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: HEALTH_COLORS[project.health] }} />
                            {project.health}
                        </span>
                        <span className="kicker text-slate-500 text-[10px]">{project.code}</span>
                    </div>
                    <h1 className="mt-2 font-display font-black text-white text-3xl md:text-4xl tracking-tighter">{project.name}</h1>
                    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-400 text-sm font-body">
                        <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />{project.location}</div>
                        <div className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />{project.client}</div>
                        <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />{project.start} → {project.end}</div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                            ["Progress", `${project.progress}%`],
                            ["Phase", project.phase],
                            ["CPI", project.cpi.toFixed(2)],
                            ["SPI", project.spi.toFixed(2)],
                            ["Value", fmtMoney(project.value_usd)],
                        ].map(([k, v]) => (
                            <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                <div className="mt-1 font-display font-black text-white text-lg tracking-tight">{v}</div>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={runHealth}
                        disabled={aiBusy}
                        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <Sparkles className="h-4 w-4" strokeWidth={2} />
                        {aiBusy ? "Analysing…" : "Run AI Project Health Assessment"}
                    </button>
                </div>
            </div>

            {ai && (
                <div className="glass rounded-xl p-6">
                    <div className="kicker text-[var(--sgsb-gold)]">AI · Project Health Assessment</div>
                    <pre className="mt-3 whitespace-pre-wrap text-slate-200 font-body text-sm leading-relaxed">{ai}</pre>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <Panel title="Packages" rows={packages.map((p) => [p.ref, p.name, `${p.progress}%`, fmtMoney(p.value_usd)])} headers={["Ref", "Package", "Progress", "Value"]} />
                <Panel title="Contracts" rows={contracts.map((c) => [c.ref, c.contractor, c.status, fmtMoney(c.value_usd)])} headers={["Ref", "Contractor", "Status", "Value"]} />
                <Panel title="Engineering deliverables" rows={deliverables.map((d) => [d.ref, d.title, d.discipline, d.status])} headers={["Ref", "Deliverable", "Discipline", "Status"]} />
                <Panel title="RFIs" rows={rfis.map((r) => [r.ref, r.subject, r.discipline, r.status])} headers={["Ref", "Subject", "Discipline", "Status"]} />
            </div>
        </div>
    );
}

function Panel({ title, headers, rows }) {
    return (
        <div className="glass rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-800/60 flex items-center justify-between">
                <h3 className="font-display font-black text-white tracking-tight">{title}</h3>
                <span className="kicker text-slate-500 text-[10px]">{rows.length} records</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm font-body">
                    <thead>
                        <tr className="text-slate-500 kicker text-[10px]">
                            {headers.map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                {r.map((c, j) => <td key={j} className="px-4 py-2.5 text-slate-200">{c}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
